/**
 * MEVIA V2 - Assessment Configuration Panel
 * 
 * Admin panel for configuring:
 * - Active evaluation types (culture, personality, or both)
 * - Culture evaluation methods (tokens, pairwise, or both)
 * - Evaluation periods management
 * - Company Identity Profile
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  ArrowLeft, Settings, GitCompare, Coins,
  Save, Check, RefreshCw, Info, AlertCircle, Target, Brain, Building2,
  BookOpen, FolderTree, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import EvaluationCyclesPanel from './EvaluationCyclesPanel';
import CompanyIdentityProfile from './CompanyIdentityProfile';
import CatalogsManagement from './CatalogsManagement';
import OrganizationalStructure from './OrganizationalStructure';
import MassImport from './MassImport';
import RolesPermissionsPanel from './RolesPermissionsPanel';
import NOM035ConfigPage from './NOM035ConfigPage';
// PulseSurveysPanel removed — Pulse config moved to PulsoDashboard (M14 C8)
import CompanyOnboarding from './CompanyOnboarding';
import { Shield, RotateCcw, FileCheck, Activity, Rocket } from 'lucide-react';
import logger from '../utils/logger';

const TEXTS = {
  es: {
    title: 'Configuración',
    subtitle: 'Gestiona evaluaciones, periodos, catálogos y estructura',
    back: 'Volver',
    refresh: 'Actualizar',
    
    // Tabs
    evaluationTypes: 'Tipos de Evaluación',
    evaluationPeriods: 'Periodos de Evaluación',
    evaluationCycles: 'Ciclos de Evaluacion',
    nom035: 'NOM-035',
    pulseSurveys: 'Pulso',
    companyIdentity: 'Identidad Corporativa',
    companyOnboarding: 'Perfil Estratégico',
    catalogs: 'Catálogos',
    structure: 'Estructura Org.',
    massImport: 'Importación',
    rolesPermissions: 'Roles y Permisos',
    
    // Evaluation Types
    activeEvaluations: 'Evaluaciones Activas',
    activeEvaluationsDesc: 'Selecciona qué tipos de evaluación estarán disponibles para tus empleados y candidatos. Puedes activar uno, otro o ambos.',
    cultureEval: 'Evaluación de Cultura',
    cultureEvalDesc: 'Evalúa la alineación cultural del empleado con la empresa a través de 10 dimensiones.',
    personalityEval: 'Evaluación de Personalidad',
    personalityEvalDesc: 'Evalúa el perfil de personalidad basado en el Eneagrama.',
    
    // Culture Methods
    cultureMethods: 'Métodos de Evaluación Cultural',
    cultureMethodsDesc: 'Selecciona qué métodos de evaluación cultural estarán disponibles. Puedes activar uno o ambos.',
    tokensMethod: 'Distribución de Puntos',
    tokensMethodDesc: 'Los usuarios distribuyen 100 puntos entre las 10 dimensiones. Más control y precisión.',
    pairwiseMethod: 'Comparación por Pares',
    pairwiseMethodDesc: 'Los usuarios comparan 45 pares de declaraciones. Más intuitivo y rápido.',
    atLeastOneMethod: 'Debes tener al menos un método de evaluación cultural activo',
    
    // Status
    active: 'Activo',
    inactive: 'Inactivo',
    enabled: 'Habilitado',
    disabled: 'Deshabilitado',
    
    // Messages
    saving: 'Guardando...',
    saved: 'Configuración guardada',
    error: 'Error al guardar',
    saveConfig: 'Guardar Configuración',
    atLeastOne: 'Debes tener al menos un tipo de evaluación activo',
    
    // Access
    accessDenied: 'Acceso Denegado',
    accessDeniedMsg: 'Solo los administradores pueden acceder a esta configuración.'
  },
  en: {
    title: 'Settings',
    subtitle: 'Manage evaluations, periods, catalogs and structure',
    back: 'Back',
    refresh: 'Refresh',
    
    // Tabs
    evaluationTypes: 'Evaluation Types',
    evaluationPeriods: 'Evaluation Periods',
    evaluationCycles: 'Evaluation Cycles',
    nom035: 'NOM-035',
    pulseSurveys: 'Pulse',
    companyIdentity: 'Corporate Identity',
    companyOnboarding: 'Strategic Profile',
    catalogs: 'Catalogs',
    structure: 'Org. Structure',
    massImport: 'Import',
    rolesPermissions: 'Roles & Permissions',
    
    // Evaluation Types
    activeEvaluations: 'Active Evaluations',
    activeEvaluationsDesc: 'Select which evaluation types will be available for your employees and candidates. You can enable one, the other, or both.',
    cultureEval: 'Culture Assessment',
    cultureEvalDesc: 'Evaluates the employee\'s cultural alignment with the company through 10 dimensions.',
    personalityEval: 'Personality Assessment',
    personalityEvalDesc: 'Evaluates the personality profile based on the Enneagram.',
    
    // Culture Methods
    cultureMethods: 'Culture Evaluation Methods',
    cultureMethodsDesc: 'Select which culture evaluation methods will be available. You can enable one or both.',
    tokensMethod: 'Point Distribution',
    tokensMethodDesc: 'Users distribute 100 points among 10 dimensions. More control and precision.',
    pairwiseMethod: 'Pairwise Comparison',
    pairwiseMethodDesc: 'Users compare 45 pairs of statements. More intuitive and faster.',
    atLeastOneMethod: 'You must have at least one culture evaluation method active',
    
    // Status
    active: 'Active',
    inactive: 'Inactive',
    enabled: 'Enabled',
    disabled: 'Disabled',
    
    // Messages
    saving: 'Saving...',
    saved: 'Configuration saved',
    error: 'Error saving',
    saveConfig: 'Save Configuration',
    atLeastOne: 'You must have at least one evaluation type active',
    
    // Access
    accessDenied: 'Access Denied',
    accessDeniedMsg: 'Only administrators can access this configuration.'
  }
};

const AssessmentConfigPanel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = useSubscription();
  const texts = TEXTS[language] || TEXTS.es;

  // State
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = new URLSearchParams(window.location.search).get('tab');
    return tabParam || 'types';
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Evaluation types config
  const [cultureEnabled, setCultureEnabled] = useState(true);
  const [personalityEnabled, setPersonalityEnabled] = useState(true);
  
  // Culture methods config
  const [tokensMethodEnabled, setTokensMethodEnabled] = useState(true);
  const [pairwiseMethodEnabled, setPairwiseMethodEnabled] = useState(true);
  
  const [currentConfig, setCurrentConfig] = useState(null);

  // Check access
  const hasAccessToConfig = user?.rol === 'company_admin' || user?.rol === 'super_admin';

  useEffect(() => {
    if (hasAccessToConfig && user?.empresa_id) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load current method config
      const configRes = await axios.get(`/v2/assessment-config/resolve?empresa_id=${user.empresa_id}`);
      if (configRes.data.success) {
        const config = configRes.data.data;
        setCultureEnabled(config.culture_enabled !== false);
        setPersonalityEnabled(config.personality_enabled !== false);
        setTokensMethodEnabled(config.tokens_method_enabled !== false);
        setPairwiseMethodEnabled(config.pairwise_method_enabled !== false);
        setCurrentConfig(config);
      }
    } catch (error) {
      logger.error('Error loading config data:', error);
      // Set defaults if no config exists
      setCultureEnabled(true);
      setPersonalityEnabled(true);
      setTokensMethodEnabled(true);
      setPairwiseMethodEnabled(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    // Validate at least one evaluation type is enabled
    if (!cultureEnabled && !personalityEnabled) {
      toast.error(texts.atLeastOne);
      return;
    }
    
    // Validate at least one culture method is enabled (if culture is enabled)
    if (cultureEnabled && !tokensMethodEnabled && !pairwiseMethodEnabled) {
      toast.error(texts.atLeastOneMethod);
      return;
    }

    // Determine default method based on what's enabled
    let defaultMethod = 'tokens';
    if (!tokensMethodEnabled && pairwiseMethodEnabled) {
      defaultMethod = 'pairwise';
    }

    setSaving(true);
    try {
      await axios.post('/v2/assessment-config', {
        empresa_id: user.empresa_id,
        assessment_method: defaultMethod,
        culture_enabled: cultureEnabled,
        personality_enabled: personalityEnabled,
        tokens_method_enabled: tokensMethodEnabled,
        pairwise_method_enabled: pairwiseMethodEnabled,
        config_priority: 10
      });
      toast.success(texts.saved);
      setCurrentConfig({ 
        ...currentConfig, 
        culture_enabled: cultureEnabled,
        personality_enabled: personalityEnabled,
        tokens_method_enabled: tokensMethodEnabled,
        pairwise_method_enabled: pairwiseMethodEnabled
      });
    } catch (error) {
      logger.error('Error saving config:', error);
      toast.error(texts.error);
    } finally {
      setSaving(false);
    }
  };

  // Access denied screen
  if (!hasAccessToConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{texts.accessDenied}</h2>
            <p className="text-gray-600 mb-6">{texts.accessDeniedMsg}</p>
            <Button onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {texts.back}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ borderBottomColor: '#1A878D', borderBottomWidth: '2px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {texts.back}
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  {texts.title}
                </h1>
                <p className="text-sm text-gray-500">{texts.subtitle}</p>
              </div>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {texts.refresh}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('types')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'types'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Target className="w-4 h-4 inline mr-2" />
            {texts.evaluationTypes}
          </button>
          <button
            onClick={() => setActiveTab('cycles')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'cycles'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <RotateCcw className="w-4 h-4 inline mr-2" />
            {texts.evaluationCycles}
          </button>
          <button
            onClick={() => setActiveTab('onboarding')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'onboarding'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Rocket className="w-4 h-4 inline mr-2" />
            {texts.companyOnboarding}
          </button>
          <button
            onClick={() => setActiveTab('nom035')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'nom035'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileCheck className="w-4 h-4 inline mr-2" />
            {texts.nom035}
          </button>
          <button
            onClick={() => setActiveTab('identity')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'identity'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            {texts.companyIdentity}
          </button>
          <button
            onClick={() => setActiveTab('catalogs')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'catalogs'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            {texts.catalogs}
          </button>
          <button
            onClick={() => setActiveTab('structure')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'structure'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FolderTree className="w-4 h-4 inline mr-2" />
            {texts.structure}
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'import'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            {texts.massImport}
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'roles'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            {texts.rolesPermissions}
          </button>
        </div>

        {/* Evaluation Types Tab */}
        {activeTab === 'types' && (
          <div className="space-y-6">
            {/* Active Evaluations Card */}
            <Card>
              <CardHeader>
                <CardTitle>{texts.activeEvaluations}</CardTitle>
                <CardDescription>{texts.activeEvaluationsDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Culture Evaluation Toggle */}
                <div className={`p-6 rounded-xl border-2 transition-all ${
                  cultureEnabled 
                    ? 'border-teal-500 bg-teal-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        cultureEnabled ? 'bg-teal-500 text-white' : 'bg-gray-300 text-gray-500'
                      }`}>
                        <Target className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{texts.cultureEval}</h3>
                        <p className="text-sm text-gray-600 mt-1">{texts.cultureEvalDesc}</p>
                        <Badge 
                          variant={cultureEnabled ? "default" : "secondary"} 
                          className={`mt-2 ${cultureEnabled ? 'bg-teal-600' : ''}`}
                        >
                          {cultureEnabled ? texts.enabled : texts.disabled}
                        </Badge>
                      </div>
                    </div>
                    <Switch
                      checked={cultureEnabled}
                      onCheckedChange={setCultureEnabled}
                      data-testid="culture-toggle"
                    />
                  </div>
                </div>

                {/* Personality Evaluation Toggle */}
                <div className={`p-6 rounded-xl border-2 transition-all ${
                  personalityEnabled 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        personalityEnabled ? 'bg-purple-500 text-white' : 'bg-gray-300 text-gray-500'
                      }`}>
                        <Brain className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{texts.personalityEval}</h3>
                        <p className="text-sm text-gray-600 mt-1">{texts.personalityEvalDesc}</p>
                        <Badge 
                          variant={personalityEnabled ? "default" : "secondary"} 
                          className={`mt-2 ${personalityEnabled ? 'bg-purple-600' : ''}`}
                        >
                          {personalityEnabled ? texts.enabled : texts.disabled}
                        </Badge>
                      </div>
                    </div>
                    <Switch
                      checked={personalityEnabled}
                      onCheckedChange={setPersonalityEnabled}
                      data-testid="personality-toggle"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Culture Methods Card - Only show if culture is enabled */}
            {cultureEnabled && (
              <Card>
                <CardHeader>
                  <CardTitle>{texts.cultureMethods}</CardTitle>
                  <CardDescription>{texts.cultureMethodsDesc}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Tokens Method Toggle */}
                  <div className={`p-5 rounded-xl border-2 transition-all ${
                    tokensMethodEnabled 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          tokensMethodEnabled ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-500'
                        }`}>
                          <Coins className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{texts.tokensMethod}</h3>
                          <p className="text-sm text-gray-600 mt-1">{texts.tokensMethodDesc}</p>
                          <Badge variant="secondary" className="mt-2">~45 min</Badge>
                        </div>
                      </div>
                      <Switch
                        checked={tokensMethodEnabled}
                        onCheckedChange={setTokensMethodEnabled}
                        data-testid="tokens-method-toggle"
                      />
                    </div>
                  </div>

                  {/* Pairwise Method Toggle */}
                  <div className={`p-5 rounded-xl border-2 transition-all ${
                    pairwiseMethodEnabled 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          pairwiseMethodEnabled ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
                        }`}>
                          <GitCompare className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{texts.pairwiseMethod}</h3>
                          <p className="text-sm text-gray-600 mt-1">{texts.pairwiseMethodDesc}</p>
                          <Badge variant="secondary" className="mt-2">~30 min</Badge>
                        </div>
                      </div>
                      <Switch
                        checked={pairwiseMethodEnabled}
                        onCheckedChange={setPairwiseMethodEnabled}
                        data-testid="pairwise-method-toggle"
                      />
                    </div>
                  </div>

                  {/* Warning if neither method is enabled */}
                  {!tokensMethodEnabled && !pairwiseMethodEnabled && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <p className="text-sm text-amber-800">{texts.atLeastOneMethod}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveConfig}
                disabled={saving || (!cultureEnabled && !personalityEnabled) || (cultureEnabled && !tokensMethodEnabled && !pairwiseMethodEnabled)}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="save-config-btn"
              >
                {saving ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> {texts.saving}</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> {texts.saveConfig}</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Evaluation Cycles Tab (MEVIA v5.0) */}
        {activeTab === 'cycles' && (
          <div className="space-y-6">
            <EvaluationCyclesPanel />
          </div>
        )}

        {/* Company Onboarding Tab (MEVIA v5.0 - Fase 1) */}
        {activeTab === 'onboarding' && user?.empresa_id && (
          <div className="space-y-6">
            <CompanyOnboarding 
              empresaId={user.empresa_id} 
              onComplete={() => setActiveTab('types')}
            />
          </div>
        )}

        {/* NOM-035 Tab (MEVIA v5.0 - Módulo 8) */}
        {activeTab === 'nom035' && (
          <div className="space-y-6">
            <NOM035ConfigPage />
          </div>
        )}

        {/* Company Identity Tab */}
        {activeTab === 'identity' && (
          <div className="space-y-6">
            <CompanyIdentityProfile />
          </div>
        )}

        {/* Catalogs Tab */}
        {activeTab === 'catalogs' && (
          <div className="space-y-6">
            <CatalogsManagement />
          </div>
        )}

        {/* Organizational Structure Tab */}
        {activeTab === 'structure' && (
          <div className="space-y-6">
            <OrganizationalStructure />
          </div>
        )}

        {/* Mass Import Tab */}
        {activeTab === 'import' && (
          <div className="space-y-6">
            <MassImport />
          </div>
        )}

        {/* Roles & Permissions Tab */}
        {activeTab === 'roles' && (
          <div className="space-y-6">
            <RolesPermissionsPanel />
          </div>
        )}
      </main>
    </div>
  );
};

export default AssessmentConfigPanel;
